import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Alert,
  Grid,
  LinearProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { formatCurrency } from "../../../../../utils/mockWalletData";

/**
 * ChannelCommissionConfirmation Tab
 * Shows pending channel partner commission payments
 * VIEW-ONLY for Thaklis Admins
 */

function ExpandableRow({ commission }) {
  const [open, setOpen] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING_APPROVAL":
        return "warning";
      default:
        return "default";
    }
  };

  // Calculate progress towards month end
  const { daysCompleted, daysInPeriod } = commission.commissionPeriod;
  const monthProgress = (daysCompleted / daysInPeriod) * 100;

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { bgcolor: "grey.50" },
          borderLeft: "4px solid #7b1fa2",
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>
          {commission.channelPartnerName}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {formatCurrency(commission.amount)}
        </TableCell>
        <TableCell>
          <Typography variant="caption">
            {commission.commissionPeriod.month}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={monthProgress}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 40 }}>
              {daysCompleted}d
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={commission.commissionBreakdown.numberOfTickets}
            size="small"
            variant="outlined"
            color="primary"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={commission.status}
            size="small"
            color={getStatusColor(commission.status)}
          />
        </TableCell>
      </TableRow>

      {/* Expandable Details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              {/* Commission Summary */}
              <Box sx={{ bgcolor: "secondary.light", p: 1.5, borderRadius: 1, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Ticket Value
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(commission.commissionBreakdown.totalTicketValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Commission Rate
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {(commission.commissionBreakdown.commissionRate * 100).toFixed(2)}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Number of Tickets
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {commission.commissionBreakdown.numberOfTickets}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Commission Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "secondary.main" }}
                      >
                        {formatCurrency(commission.commissionBreakdown.commissionAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Period Details */}
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Commission Period
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.commissionPeriod.startDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.commissionPeriod.endDate}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Partner Bank Details */}
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Partner Bank Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.partnerBankDetails?.accountName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.partnerBankDetails?.accountNumber}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bank Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.partnerBankDetails?.bankName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      IBAN
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {commission.partnerBankDetails?.iban}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Ticket Breakdown */}
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Ticket Breakdown
              </Typography>
              <TableContainer sx={{ bgcolor: "grey.50", mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.200" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Ticket ID</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        Value
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        Commission
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commission.commissionBreakdown.tickets.map((ticket) => (
                      <TableRow key={ticket.ticketId}>
                        <TableCell>{ticket.ticketId}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(ticket.value)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(ticket.commission)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: "grey.100", fontWeight: "bold" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(commission.commissionBreakdown.totalTicketValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(commission.commissionBreakdown.commissionAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Payment Due Details */}
              <Box sx={{ bgcolor: "info.light", p: 1.5, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Payment Due Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {commission.paymentDueDate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Days Until Due
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color:
                            commission.daysUntilDue <= 7
                              ? "error.main"
                              : "success.main",
                        }}
                      >
                        {commission.daysUntilDue > 0
                          ? `${commission.daysUntilDue} days`
                          : "Overdue"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const ChannelCommissionConfirmation = ({ data }) => {
  if (!data || data.items.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No pending channel commission payments at this time.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, p: 1.5, bgcolor: "secondary.light", borderRadius: 1, border: "1px solid", borderColor: "secondary.lighter" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "secondary.dark" }}>
          Channel Commissions: {formatCurrency(data.total)} ({data.count} partner{data.count > 1 ? "s" : ""})
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "primary.main" }}>
            <TableRow>
              <TableCell width="40" sx={{ fontWeight: "bold", color: "white" }} />
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Partner Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "white" }}>
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Period</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Progress</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "white" }}>
                Tickets
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((commission) => (
              <ExpandableRow key={commission.id} commission={commission} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChannelCommissionConfirmation;
